import _orderBy from 'lodash/orderBy';
import styles from './SuccessView.module.scss';

import config from 'config';

import Link from 'next/link';
import { useDispatch } from 'react-redux';
import Confetti from 'react-confetti';
import * as toast from 'legacy/common/utils/toast';
import { format } from 'legacy/common/utils/datetime';

import { EventForUser, TicketInfoRequiredResponse } from 'legacy/events/types';

import { hideModal, showModal } from 'legacy/actions/modalActions';

import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { useGetMyTicketForEvent } from 'legacy/tickets/hooks';

import {
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaRegCalendar,
  FaShareAlt
} from 'react-icons/fa';
import { useEffect, useRef, useState } from 'react';
import { getAppStoreLink } from 'legacy/common/utils/platform';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { copyToClipboard } from 'legacy/common/utils/clipboard';
import GuestSuggestions from './GuestSuggestions';

type SuccessViewProps = {
  event: EventForUser;
  price: string;
  onClose: () => void;
  isEligibleForOffers?: boolean;
  onClickViewEvent: () => void;
  onFillRequestedInfoSuccess?: () => void;
  ticketInfoRequired: TicketInfoRequiredResponse;
};

const SuccessView: React.FC<SuccessViewProps> = ({
  event,
  ticketInfoRequired,
  price,
  onFillRequestedInfoSuccess,
  onClickViewEvent,
  onClose,
  isEligibleForOffers
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const confettiZoneRef = useRef<HTMLDivElement>(null);

  const { data: ticket } = useGetMyTicketForEvent(event._id);
  const shareToken = ticket?.additionalTickets.length
    ? _orderBy(ticket.additionalTickets, 'purchasedAt', 'desc')[0].shareToken // NOTE: Hacky way to obtain the share token for the purchase
    : undefined;

  const [showDetails, setShowDetails] = useState(false);
  const toggleDetails = () => {
    setShowDetails((showDetails) => !showDetails);
  };

  useEffect(() => {
    setHeight(confettiZoneRef.current.clientHeight);
    setWidth(confettiZoneRef.current.clientWidth);
  }, [showDetails]);

  const dispatch = useDispatch();

  const shareUrl = `${config.SITE_URL}tickets/claim/${shareToken}`;

  const renderButtons = () => {
    if (ticketInfoRequired?.ticketNotes.required) {
      return (
        <Button
          type={ButtonType.Primary}
          onClick={() => {
            dispatch(
              showModal('TICKET_NOTES', {
                event,
                guestQuestions: ticketInfoRequired?.ticketNotes?.guestQuestions,
                onClose: () => {
                  dispatch(hideModal());
                },
                onSuccess: () => {
                  onFillRequestedInfoSuccess?.();
                  dispatch(hideModal());
                }
              })
            );
            onClose();
          }}
        >
          <div className="flex items-center justify-center">
            Fill out Requested Information
          </div>
        </Button>
      );
    }

    if (ticketInfoRequired?.googleForms?.required) {
      return (
        <Button
          type={ButtonType.Primary}
          onClick={() => {
            dispatch(
              showModal('FILL_FORM', {
                event,
                onSuccess: () => {
                  onFillRequestedInfoSuccess?.();

                  dispatch(hideModal());
                }
              })
            );
            onClose();
          }}
        >
          <div className="flex items-center justify-center">
            Fill out Requested Information
          </div>
        </Button>
      );
    }

    return (
      <Button
        type={ButtonType.Primary}
        onClick={() => {
          onClose();
          onClickViewEvent();
        }}
        testId="view-event"
      >
        <div className="flex items-center justify-center">
          <FaRegCalendar className="mr-2" />
          View Event
        </div>
      </Button>
    );
  };

  return (
    <>
      <div
        className="h-full relative flex flex-col items-center justify-start overflow-auto pb-40"
        data-testid="view-checkout-success"
      >
        <div
          className="w-full bg-primary-200 flex flex-col items-center p-10"
          id="confetti-zone"
          ref={confettiZoneRef}
        >
          <Confetti
            width={width}
            height={height}
            numberOfPieces={100}
            colors={['#643DC4', '#F98700', '#7994f7']}
            drawShape={(ctx) => {
              ctx.beginPath();
              ctx.arc(0.2, 0.2, 4, 0, 2 * Math.PI);
              ctx.fill();

              ctx.closePath();
            }}
          />
          <div className={styles.scaleIn}>
            <div className="flex items-center justify-center">
              <div className={styles.eventPhoto}>
                <img
                  className="w-24 h-24 object-cover rounded-full shadow-xl"
                  src={event.photo.url}
                  alt={event.title}
                />
              </div>

              <div className={styles.checkMark}>
                <div className="w-24 h-24 object-cover rounded-full bg-primary-400 flex items-center justify-center shadow-xl">
                  <FaCheck size={40} color="white" />
                </div>
              </div>
            </div>
          </div>

          <h1 className="mt-10 text-2xl font-bold">You&apos;re all set!</h1>
          <p className="mt-4 text-lg text-gray-600 font-normal text-center">
            You have reserved your tickets to {event.title}
          </p>

          {ticket?.additionalTickets.length > 0 && (
            <div className="mt-8 flex flex-col items-center">
              <p className="text-sm text-gray-500 text-center px-4 md:px-52 font-normal">
                Share your additional tickets to let your friends claim them and
                meet you at the event!
              </p>

              <div className="w-80 mt-10 md:mt-4">
                <Button
                  testId="copy-to-clipboard-text"
                  onClick={() => {
                    if (navigator.share) {
                      navigator?.share({
                        url: shareUrl,
                        title: event.title
                      });
                      return;
                    }

                    copyToClipboard(shareUrl);
                    toast.success(`Copied link to clipboard!`);
                  }}
                >
                  <div className="flex items-center justify-center">
                    <FaShareAlt className="mr-2" /> Share Additional Tickets
                  </div>
                </Button>
              </div>
            </div>
          )}

          <div className="mt-8">
            <FadeIn>
              <p
                className="cursor-pointer text-base font-semibold text-primary-400 flex items-center justify-center"
                onClick={toggleDetails}
              >
                {showDetails ? (
                  <>
                    Hide Details <FaChevronUp className="ml-1" />
                  </>
                ) : (
                  <>
                    Show Details <FaChevronDown className="ml-1" />
                  </>
                )}
              </p>
            </FadeIn>
          </div>

          {showDetails && (
            <div className="w-full mt-4">
              <p className="text-lg">Details</p>
              <div className="mt-4">
                <p className="text-base text-gray-500">Date</p>
                <p className="text-base font-semibold">
                  📅 {format(ticket?.event?.startDate)}
                </p>
              </div>

              <div className="mt-3">
                <p className="text-base text-gray-500">Location</p>
                <p className="text-base font-semibold">
                  📍 {ticket?.event?.address}
                </p>
              </div>

              <div className="mt-3">
                <p className="text-base text-gray-500">Total Cost</p>
                <p className="text-base font-semibold">💰 ${price}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col flex-1 md:px-16 md:mt-4 px-8 w-full justify-between">
          {isEligibleForOffers && (
            <div className="my-4 p-[4px] max-w-[345px] mx-auto rounded-xl bg-gradient-to-r from-[#172c7a] via-[#EFF2FF] to-[#1638AC]">
              <div className="flex flex-col flex-1 p-5 rounded-xl bg-gradient-to-b from-[#04144e] to-[#0b257c] text-white">
                <h3 className="text-white text-lg font-bold pb-4">
                  You have exclusive offers waiting!
                </h3>
                <p className="pb-6 text-base font-normal">
                  Unlock rewards by becoming a Promoter! The more tickets you
                  sell, the more you earn—bring more people to the event and
                  rack up some serious perks! Click <strong>Learn More</strong>{' '}
                  to see how it works.
                </p>
                <Link
                  href="/ticketsTab/offers/unclaimed"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-primary-400 text-white hover:text-white text-base font-bold px-4 py-3 rounded-full flex items-center justify-center"
                >
                  Learn More
                </Link>
              </div>
            </div>
          )}

          {/* Mobile View */}
          <a
            href={getAppStoreLink()}
            target="_blank"
            rel="noreferrer"
            className="block md:hidden mt-4 mb-10 cursor-pointer mx-auto"
          >
            <div className="flex shadow-lg rounded-lg overflow-hidden border-2 border-primary-400">
              <img
                src="/img/events/download-app-vertical.png"
                className="object-cover h-full w-28"
              />
              <div className="pt-4 px-4">
                <p className="mt-4 text-base font-semibold">Download the app</p>
                <p className="mt-2 text-sm">
                  Open in the app to get the full experience
                </p>
              </div>
            </div>
          </a>

          {/* Desktop View */}
          <a
            href={getAppStoreLink()}
            target="_blank"
            rel="noreferrer"
            className="hidden md:flex mb-10 cursor-pointer mx-auto"
          >
            <div className="shadow-lg rounded-lg overflow-hidden border-2 border-primary-400">
              <img
                src="/img/events/download-app.png"
                className="w-full object-cover"
              />
              <div className="p-4">
                <p className="mt-4 text-base font-semibold">Download the app</p>
                <p className="mt-2 text-sm">
                  Open in the app to get the full experience and never miss out
                  on an event.
                </p>
              </div>
            </div>
          </a>

          <GuestSuggestions event={event} />
        </div>
      </div>

      <div className="mt-10 absolute bottom-0 w-full">
        <div className={styles.boxShadow}>
          <div className="px-4 w-full py-8 bg-white">{renderButtons()}</div>
        </div>
      </div>
    </>
  );
};

export default SuccessView;
